import { Action } from '../../models/core/core.type';
import {
  ProposalResiduePlanCodeEnum,
  ProposalResiduePlanQuotationType,
  ProposalResiduePlanType,
  ProposalResidueType,
} from '../../models/core/proposals.type';

const matchesByKey = (
  left: string | number | null | undefined,
  right: string | number | null | undefined
) => {
  if (
    left === null ||
    left === undefined ||
    right === null ||
    right === undefined ||
    left === '' ||
    right === ''
  ) {
    return false;
  }

  return `${left}` === `${right}`;
};

const matchesPlanItem = (
  planItem: {
    id?: string | number | null;
    idTemporaria?: string | number | null;
    relatedId?: string | number | null;
    residueId?: string | number | null;
  },
  quotationItem: {
    id?: string | number | null;
    idTemporaria?: string | number | null;
    relatedId?: string | number | null;
    residueId?: string | number | null;
  }
) =>
  matchesByKey(planItem.relatedId, quotationItem.relatedId) ||
  matchesByKey(planItem.id, quotationItem.relatedId) ||
  matchesByKey(planItem.idTemporaria, quotationItem.relatedId) ||
  matchesByKey(planItem.id, quotationItem.id) ||
  matchesByKey(planItem.id, quotationItem.idTemporaria) ||
  matchesByKey(planItem.idTemporaria, quotationItem.id) ||
  matchesByKey(planItem.idTemporaria, quotationItem.idTemporaria) ||
  matchesByKey(planItem.residueId, quotationItem.residueId);

const getPlanCode = (plan: ProposalResiduePlanType) =>
  plan.codigoPlanoTipo || ProposalResiduePlanCodeEnum.Mobilization;

const isMasterPlan = (plan: ProposalResiduePlanType) =>
  getPlanCode(plan) === ProposalResiduePlanCodeEnum.Mobilization;

const hasAnyPlanItem = (plan: ProposalResiduePlanType) =>
  plan.acondicionamentos.some((x) => (x.action || Action.Nothing) !== Action.Delete) ||
  plan.equipamentos.some((x) => (x.action || Action.Nothing) !== Action.Delete) ||
  (!!plan.veiculo && (plan.veiculo.action || Action.Nothing) !== Action.Delete) ||
  (!!plan.tratamento && (plan.tratamento.action || Action.Nothing) !== Action.Delete) ||
  (!!plan.destinoFinal && (plan.destinoFinal.action || Action.Nothing) !== Action.Delete);

const hasCompletePlanDefinition = (plan: ProposalResiduePlanType) => {
  if (isMasterPlan(plan)) {
    return (
      plan.acondicionamentos.some((x) => (x.action || Action.Nothing) !== Action.Delete) ||
      plan.equipamentos.some((x) => (x.action || Action.Nothing) !== Action.Delete)
    );
  }

  return (
    (!!plan.veiculo && (plan.veiculo.action || Action.Nothing) !== Action.Delete) ||
    (!!plan.tratamento && (plan.tratamento.action || Action.Nothing) !== Action.Delete) ||
    (!!plan.destinoFinal && (plan.destinoFinal.action || Action.Nothing) !== Action.Delete)
  );
};

const isQuotationEffectivelyComplete = (
  plan: ProposalResiduePlanType,
  quotation: ProposalResiduePlanQuotationType
) => [quotation].length > 0 && hasCompletePlanDefinition(plan);

const normalizePlanQuotationCompletion = (plan: ProposalResiduePlanType): ProposalResiduePlanType => ({
  ...plan,
  cotacoes: plan.cotacoes.map((quotation) => ({
    ...quotation,
    completo: isQuotationEffectivelyComplete(plan, quotation),
  })),
});

const normalizeResiduePlansQuotationCompletion = (residue: ProposalResidueType): ProposalResidueType => ({
  ...residue,
  planos: residue.planos.map(normalizePlanQuotationCompletion),
});

const normalizeResiduePlanPrimaries = (residue: ProposalResidueType): ProposalResidueType => {
  const visiblePlans = residue.planos.filter((plan) => (plan.action || Action.Nothing) !== Action.Delete);
  const mobilizationPlans = visiblePlans.filter(
    (plan) => getPlanCode(plan) === ProposalResiduePlanCodeEnum.Mobilization
  );
  const regularPlans = visiblePlans.filter(
    (plan) => getPlanCode(plan) !== ProposalResiduePlanCodeEnum.Mobilization
  );
  const primaryRegularPlan = regularPlans.find((plan) => plan.primario) || regularPlans[0] || null;

  return {
    ...residue,
    planos: residue.planos.map((plan) => {
      if ((plan.action || Action.Nothing) === Action.Delete) {
        return plan;
      }

      const isMobilizationPlan = getPlanCode(plan) === ProposalResiduePlanCodeEnum.Mobilization;
      const shouldBePrimary = isMobilizationPlan
        ? mobilizationPlans.some(
            (entry) =>
              matchesByKey(entry.id, plan.id) ||
              matchesByKey(entry.idTemporaria, plan.idTemporaria) ||
              matchesByKey(entry.idPropostaResiduoPlano, plan.idPropostaResiduoPlano)
          )
        : !!primaryRegularPlan &&
          (matchesByKey(primaryRegularPlan.id, plan.id) ||
            matchesByKey(primaryRegularPlan.idTemporaria, plan.idTemporaria) ||
            matchesByKey(primaryRegularPlan.idPropostaResiduoPlano, plan.idPropostaResiduoPlano));

      return {
        ...plan,
        primario: shouldBePrimary,
      };
    }),
  };
};

export {
  getPlanCode,
  hasAnyPlanItem,
  hasCompletePlanDefinition,
  isMasterPlan,
  isQuotationEffectivelyComplete,
  matchesByKey,
  matchesPlanItem,
  normalizePlanQuotationCompletion,
  normalizeResiduePlanPrimaries,
  normalizeResiduePlansQuotationCompletion,
};
